import { Component, EventEmitter, Input, OnInit, Output, Renderer2 } from '@angular/core';
import { BaseComponent } from 'src/app/core/components/base/base.component';
import { ToasterTypes } from 'src/app/core/enum/toaster-type.enum';
import { InvoiceService } from 'src/app/data/service/invoice.service';
import { ToasterMessageService } from 'src/app/shared/toaster-message/toaster-message.service';
import { Invoice } from '../../models/invoice/invoice';
import { OrganizationService } from 'src/app/data/service/organization.service';
@Component({
  selector: 'app-invoice-preivew-web',
  templateUrl: './invoice-preview-web.component.html',
  styleUrls: ['./invoice-preview-web.component.scss']
})
export class InvoicePreviewWebComponent extends BaseComponent implements OnInit {
  @Input() isHorizontalDirection: boolean = false;
  @Input() invoice: Invoice = new Invoice(this._organizationService.selectedOrganization);
  /** The × closes the preview on its own. A host whose panel has its own close hides it. */
  @Input() showClose: boolean = true;
  @Output() isClosed = new EventEmitter<boolean>();
  @Output() closeFormEvent = new EventEmitter<string>();

  private invoicePdfUrl: string = '';
  private fileName: string = '';
  isLoaded: boolean = false;
  isPdf: boolean = false;
  /** Why there is nothing to show. Null while loading or once a document is on screen. */
  previewError: string | null = null;
  tempFile: string;
  showToolTipDelay: number = 400;
  pageScale: number = 100;

  constructor(
    private _toasterMessageService: ToasterMessageService,
    private _invoiceService: InvoiceService,
    private _organizationService: OrganizationService,
    private renderer: Renderer2,
  ) {
    super();
  }

  ngOnInit() {
    this.subscribeToFileUpdates();
  }

  subscribeToFileUpdates() {
    this.isLoaded = false;
    this.previewError = null;
    this.invoicePdfUrl = this.invoice.pdfUrl;
    this.fileName = `${ this.invoice.supplierName} ${ this.invoice.invoiceNumber}`;
    this.downloadInvoice(this.invoice.pdfUrl);
  }

  /**
   * Fetch the document for the preview.
   *
   * WITHOUT an organisation id, which is what downloadPdfInvoice() has always done — and why
   * Download worked on a bill whose preview came up blank. The id identifies the document on
   * its own; scoping the lookup to the SELECTED organisation finds nothing when that is a
   * group and the bill belongs to one of its members, which is exactly the case on Bills to
   * Pay. The two calls hit the same endpoint, so they now ask it the same question.
   */
  downloadInvoice(id: string) {
    this._invoiceService.downloadFile(id, '')
      .pipe(this.untilThis)
      .subscribe(
        (blob: Blob) => {
          this._setPreview(blob);
        },
        (e: Error) => {
          this.previewError = 'This document could not be loaded.';
          this._toasterMessageService.displayToasterState(ToasterTypes.Error, 'Error', e.message);
        }
      )
  }

  /**
   * Show the document — or say why not.
   *
   * What a file is comes from its first bytes, not the type it was stored with: older uploads
   * were stored as application/octet-stream whatever they were, and handing an image (or an
   * error page) to the PDF viewer left the panel empty with nothing to explain it. A PDF or an
   * image is shown; anything else is reported, and Download still offers the file itself.
   */
  private _setPreview(blob: Blob) {
    if (!blob || !blob.size) {
      this._showPreviewError('This document could not be loaded. Download still works.');
      return;
    }

    const head = blob.slice(0, 12);
    const reader = new FileReader();
    reader.onloadend = () => {
      const kind = InvoicePreviewWebComponent.sniff(new Uint8Array(reader.result as ArrayBuffer));
      if (!kind) {
        this._showPreviewError('This file can\'t be shown here. Download it to open it.');
        return;
      }
      // Re-typed, so an image stored as octet-stream reaches the <img> as an image.
      this._readAsDataUrl(kind.mime === blob.type ? blob : new Blob([blob], { type: kind.mime }), kind.isPdf);
    };
    reader.onerror = () => this._showPreviewError('This document could not be read. Download still works.');
    reader.readAsArrayBuffer(head);
  }

  private _readAsDataUrl(blob: Blob, isPdf: boolean) {
    const reader = new FileReader();
    reader.onloadend = () => {
      this.tempFile = reader?.result as string;
      this.isPdf = isPdf;
      this.previewError = null;
      this.isLoaded = true;
    };
    reader.onerror = () => this._showPreviewError('This document could not be read. Download still works.');
    reader.readAsDataURL(blob);
  }

  /** pdf.js could not open what looked like a PDF (damaged, password-protected). */
  onPdfError() {
    this._showPreviewError('This PDF could not be shown here. Download it to open it.');
  }

  private _showPreviewError(message: string) {
    this.isLoaded = false;
    this.previewError = message;
  }

  /** The file's type from its signature: PDF, PNG, JPEG, GIF or WebP; null for anything else. */
  static sniff(b: Uint8Array): { mime: string, isPdf: boolean } | null {
    const starts = (...sig: number[]) => b.length >= sig.length && sig.every((x, i) => b[i] === x);
    if (starts(0x25, 0x50, 0x44, 0x46)) { return { mime: 'application/pdf', isPdf: true }; }
    if (starts(0x89, 0x50, 0x4E, 0x47)) { return { mime: 'image/png', isPdf: false }; }
    if (starts(0xFF, 0xD8, 0xFF)) { return { mime: 'image/jpeg', isPdf: false }; }
    if (starts(0x47, 0x49, 0x46, 0x38)) { return { mime: 'image/gif', isPdf: false }; }
    if (b.length >= 12 && starts(0x52, 0x49, 0x46, 0x46) && b[8] === 0x57 && b[9] === 0x45 && b[10] === 0x42 && b[11] === 0x50) {
      return { mime: 'image/webp', isPdf: false };
    }
    return null;
  }

  downloadPdfInvoice() {
    const link = this.renderer.createElement('a');
    link.setAttribute('target', '_blank');
    link.setAttribute('href', this._invoiceService.getDownloadInvoiceUrl(this.invoicePdfUrl, "", this.fileName));
    link.setAttribute('download', this.invoicePdfUrl);
    link.click();
    link.remove();
  }

  /** One wheel notch in (+1) or out (-1), between 50% and 400%. */
  public zoomBy(direction: number)
	{
    this.pageScale = Math.min(400, Math.max(50, this.pageScale + direction * 25));
	}

  public zoomIn()
	{
    this.pageScale = this.pageScale + 25;
	}

  public zoomReset()
	{
    this.pageScale = 100;
	}

	public zoomOut()
	{
    if (this.pageScale > 100) {
      this.pageScale = this.pageScale - 25;
    }
	}

  ClosePreviewSidebar() {
    this.closeFormEvent.emit(this.invoice.id.toString());
  }
}
